import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFracas } from '../contexts/FracasContext';
import { FracasSeverity, FracasPriority, FracasAttachment } from '../types/fracas.types';
import { AlertCircle, Save, X } from 'lucide-react';
import { FileUpload, UploadedFile } from '../components/shared/FileUpload';

const FracasCreate: React.FC = () => {
    const navigate = useNavigate();
    const { addIncident } = useFracas();

    const [formData, setFormData] = useState({
        title: '',
        customerName: '',
        customerContact: '',
        customerReferenceNumber: '',
        complaintDate: new Date().toISOString().split('T')[0],
        complaintDescription: '',
        productName: '',
        partNumber: '',
        serialNumber: '',
        lotNumber: '',
        quantityAffected: 1,
        severity: 'Major' as FracasSeverity,
        priority: 'Medium' as FracasPriority,
        category: 'Field Failure',
        failureMode: '',
        problemDescription: '',
        immediateActions: '',
        location: '',
        occurredAt: new Date().toISOString().split('T')[0],
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<UploadedFile[]>([]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value) || 0 : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await addIncident({
                ...formData,
                fracasNumber: `INC-${Date.now()}`,
                status: 'Open',
                complaintDate: new Date(formData.complaintDate),
                occurredAt: formData.occurredAt ? new Date(formData.occurredAt) : undefined,
                // Required fields with defaults
                organizationId: 'default-org', // Will be replaced by actual user org
                reportedBy: 'current-user',
                reportedByName: 'Current User',
                reportedAt: new Date(),
                statusHistory: [{
                    status: 'Open',
                    changedBy: 'current-user',
                    changedByName: 'Current User',
                    changedAt: new Date(),
                    notes: 'Incident created',
                }],
                teamMembers: [],
                rootCauses: [],
                correctiveActions: [],
                attachments: attachments.map((f): FracasAttachment => ({
                    id: f.id,
                    name: f.name,
                    type: f.type,
                    url: f.url,
                    size: f.size,
                    uploadedBy: 'current-user',
                    uploadedAt: f.uploadedAt,
                    description: f.description,
                })),
                comments: [],
                tags: [],
                relatedIncidents: [],
            });

            navigate('/fracas');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create incident');
            setLoading(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-8">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Create FRACAS Incident</h1>
                <p className="text-gray-600 mt-2">
                    Failure Reporting, Analysis, and Corrective Action System
                </p>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Information */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Incident Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Brief description of the issue"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Severity *
                            </label>
                            <select
                                name="severity"
                                value={formData.severity}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="Critical">Critical - Safety/Production Stopped</option>
                                <option value="Major">Major - Significant Impact</option>
                                <option value="Minor">Minor - Low Impact</option>
                                <option value="Cosmetic">Cosmetic - Appearance Only</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Priority *
                            </label>
                            <select
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="Immediate">Immediate - Drop Everything</option>
                                <option value="High">High - Within 24 Hours</option>
                                <option value="Medium">Medium - Within 1 Week</option>
                                <option value="Low">Low - When Available</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="Field Failure">Field Failure</option>
                                <option value="Manufacturing Defect">Manufacturing Defect</option>
                                <option value="Design Issue">Design Issue</option>
                                <option value="Material Defect">Material Defect</option>
                                <option value="Assembly Error">Assembly Error</option>
                                <option value="Test Failure">Test Failure</option>
                                <option value="Customer Complaint">Customer Complaint</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Failure Mode
                            </label>
                            <input
                                type="text"
                                name="failureMode"
                                value={formData.failureMode}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., Wear, Corrosion, Fracture"
                            />
                        </div>
                    </div>
                </div>

                {/* Customer Information */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h2>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Customer Name *
                            </label>
                            <input
                                type="text"
                                name="customerName"
                                value={formData.customerName}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Customer Contact
                            </label>
                            <input
                                type="text"
                                name="customerContact"
                                value={formData.customerContact}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Email or phone"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Customer Reference Number
                            </label>
                            <input
                                type="text"
                                name="customerReferenceNumber"
                                value={formData.customerReferenceNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Complaint Date *
                            </label>
                            <input
                                type="date"
                                name="complaintDate"
                                value={formData.complaintDate}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Complaint Description *
                            </label>
                            <textarea
                                name="complaintDescription"
                                value={formData.complaintDescription}
                                onChange={handleChange}
                                required
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="What did the customer report?"
                            />
                        </div>
                    </div>
                </div>

                {/* Product Information */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Product Information</h2>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Product Name *
                            </label>
                            <input
                                type="text"
                                name="productName"
                                value={formData.productName}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Part Number
                            </label>
                            <input
                                type="text"
                                name="partNumber"
                                value={formData.partNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Serial Number
                            </label>
                            <input
                                type="text"
                                name="serialNumber"
                                value={formData.serialNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Lot Number
                            </label>
                            <input
                                type="text"
                                name="lotNumber"
                                value={formData.lotNumber}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantity Affected *
                            </label>
                            <input
                                type="number"
                                name="quantityAffected"
                                value={formData.quantityAffected}
                                onChange={handleChange}
                                required
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Location
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Where did it occur?"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Problem Description *
                            </label>
                            <textarea
                                name="problemDescription"
                                value={formData.problemDescription}
                                onChange={handleChange}
                                required
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Detailed description of the problem, symptoms, and observed defects"
                            />
                        </div>
                    </div>
                </div>

                {/* Immediate Actions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Immediate Actions (Optional)</h2>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Containment Actions Taken
                        </label>
                        <textarea
                            name="immediateActions"
                            value={formData.immediateActions}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Any immediate actions taken to contain or mitigate the issue"
                        />
                    </div>
                </div>

                {/* Attachments */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Attachments</h2>
                    <FileUpload
                        files={attachments}
                        onFilesChange={setAttachments}
                        maxFiles={10}
                        maxSizeMB={25}
                        label="Evidence & Documentation"
                        description="Upload photos of the defect, related documents, or videos"
                    />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => navigate('/fracas')}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        {loading ? 'Creating...' : 'Create Incident'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FracasCreate;
